import { QueryRequestHelper } from "@common/query-request-helper";
import { formatText } from "@common/text-helpers";
import { getColorStyle } from "@common/color";
import DeleteModal from "@common/popup/DeleteModal";
import GearLoader from "@components/common/GearLoader";
import { getValue } from "@utils/lodash";
import {
  updateFieldsSwap,
  getModulesFields,
  removeCustomField,
  updateModuleFields,
} from "@services/modules.service";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { DragDropContext, Draggable, Droppable } from "react-beautiful-dnd";
import useDraggableInPortal from "@common/useDraggableInPortal";
import DragIndicatorSvgComponent from "@assets/svg-components/drag-indicator";
import {
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  UncontrolledDropdown,
} from "reactstrap";
import { toast } from "react-toastify";

function TableFields(props: any) {
  const navigate = useNavigate();
  const renderDraggable = useDraggableInPortal();
  const [fields, setFields] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);
  const [deleteId, setDeleteId] = useState("");
  const [deleteName, setDeleteName] = useState("");

  useEffect(() => {
    getData();
  }, [props.tableFieldName]);

  const getData = async () => {
    try {
      setIsLoading(true);
      const queryRequest = QueryRequestHelper({
        entity_type: "expense_entry_table",
        field_value: props.tableFieldName,
        page_size: 100,
        page_no: 1,
      });
      const response = await getModulesFields(queryRequest);
      setFields(getValue(response, "data.fields", []));
    } catch (error) {
      setFields([]);
    } finally {
      setIsLoading(false);
    }
  };

  const buildFieldUrl = (fieldId?: string) => {
    const query = new URLSearchParams({
      field_scope: "table",
      table_field_name: props.tableFieldName,
      parent_field_id: props.parentFieldId,
    });
    const basePath = fieldId
      ? `/admin/modules/expenses/fields/detail/${fieldId}`
      : "/admin/modules/expenses/fields/add";
    return `${basePath}?${query.toString()}`;
  };

  const handleOnDragEnd = async (result: any) => {
    if (!result.destination || result.source.index === result.destination.index) {
      return;
    }

    const reorderedFields = [...fields];
    const [movedField] = reorderedFields.splice(result.source.index, 1);
    reorderedFields.splice(result.destination.index, 0, movedField);
    setFields(reorderedFields);

    try {
      const response = await updateFieldsSwap({
        swap_fields: reorderedFields.map((field: any, index: number) => ({
          id: getValue(field, "id", ""),
          seq_no: index + 1,
        })),
      });
      if (response) {
        toast.success("Column order updated");
      }
    } catch (error) {
      getData();
    }
  };

  const onChange = (fieldName: string, value: boolean, fieldIndex: number) => {
    setFields((currentFields: any) =>
      currentFields.map((field: any, index: number) =>
        index === fieldIndex ? { ...field, [fieldName]: value } : field
      )
    );
    setHasChanges(true);
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      const response = await updateModuleFields({
        fields: fields.map((field: any) => ({
          id: getValue(field, "id", ""),
          is_enabled: getValue(field, "is_enabled", false),
          is_mandatory: getValue(field, "is_mandatory", false),
        })),
      });
      if (response) {
        toast.success(getValue(response, "message", "Fields updated"));
        setHasChanges(false);
        getData();
      }
    } catch (error) {
      toast.error("Unable to update table fields");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    try {
      const response = await removeCustomField(deleteId);
      if (response) {
        toast.success(getValue(response, "message", ""));
        setIsDeleteOpen(false);
        getData();
      }
    } catch (error) {}
  };

  return (
    <section className="table-fields-panel">
      <div className="table-fields-panel__header">
        <div>
          <h2>Fields in this table</h2>
          <p>{fields.length} configured</p>
        </div>
        {getValue(props, "permissions", []).includes("update") && (
          <div className="d-flex align-items-center gap-2">
            <button
              className={`ascent-button ascent-button--header-buttons ascent-button--${
                hasChanges ? "primary" : "secondary"
              }`}
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
            >
              {isSaving ? "Saving..." : "Save"}
            </button>
            <button
              className="ascent-button ascent-button--header-buttons ascent-button--primary"
              onClick={() => navigate(buildFieldUrl())}
            >
              <span aria-hidden="true">+</span> Add Column
            </button>
          </div>
        )}
      </div>
      {isLoading ? (
        <GearLoader />
      ) : fields.length ? (
        <div className="table-fields-list m-4">
          <DragDropContext onDragEnd={handleOnDragEnd}>
            <Droppable droppableId="table-fields">
              {(droppableProvided) => (
                <div
                  ref={droppableProvided.innerRef}
                  {...droppableProvided.droppableProps}
                >
                  {fields.map((field: any, index: number) => (
                    <Draggable
                      key={getValue(field, "id", index)}
                      draggableId={String(getValue(field, "id", index))}
                      index={index}
                    >
                      {renderDraggable((draggableProvided: any) => (
                        <div className="d-flex align-items-center">
                          <p className="header_text drag_seq_text text-center">
                            {index + 1}
                          </p>
                          <div
                            className="d-flex align-items-center mb-2 drag_common_card ms-2"
                            ref={draggableProvided.innerRef}
                            {...draggableProvided.draggableProps}
                            {...draggableProvided.dragHandleProps}
                          >
                            <div className="position-relative w-100 d-flex align-items-center">
                              <div className="ms-2">
                                <DragIndicatorSvgComponent color="lightgray" />
                              </div>
                              <div className="ms-4 drag_field_name">
                                <p className="header_text__14">
                                  {getValue(field, "display_name", "")}
                                </p>
                                <p className="small_text__12">
                                  {formatText(getValue(field, "data_type", ""))}
                                </p>
                              </div>
                              <div className="ms-5">
                                <p className="small_text__14 mb-2">Enable</p>
                                <input
                                  type="checkbox"
                                  className="common-checkbox"
                                  id={`table-field-enabled-${index}`}
                                  checked={getValue(field, "is_enabled", false)}
                                  disabled={getValue(field, "is_system_field", false)}
                                  onChange={() => {
                                    const isEnabled = getValue(
                                      field,
                                      "is_enabled",
                                      false
                                    );
                                    onChange("is_enabled", !isEnabled, index);
                                    if (isEnabled) {
                                      onChange("is_mandatory", false, index);
                                    }
                                  }}
                                />
                              </div>
                              <div className="ms-5">
                                <p className="small_text__14 mb-2">Mandatory</p>
                                <input
                                  type="checkbox"
                                  className="common-checkbox"
                                  id={`table-field-mandatory-${index}`}
                                  checked={getValue(field, "is_mandatory", false)}
                                  disabled={
                                    getValue(field, "is_system_field", false) ||
                                    !getValue(field, "is_enabled", false)
                                  }
                                  onChange={() =>
                                    onChange(
                                      "is_mandatory",
                                      !getValue(field, "is_mandatory", false),
                                      index
                                    )
                                  }
                                />
                              </div>
                              <div className="position-absolute end-0 top-0">
                                <span
                                  className="small_text__10"
                                  style={getColorStyle("awaiting_approval")}
                                >
                                  {formatText(getValue(field, "field_name", ""))}
                                </span>
                              </div>
                            </div>
                          </div>
                          {getValue(props, "permissions", []).includes("update") && (
                            <div>
                              <UncontrolledDropdown>
                                <DropdownToggle>
                                  <div>
                                    <div className="common_image_container">
                                      <img
                                        src="/images/settings.svg"
                                        className="common_image"
                                      />
                                    </div>
                                  </div>
                                </DropdownToggle>
                                <DropdownMenu className="bulk-dropdown_field">
                                  <DropdownItem
                                    className="bulk-dropdown_item"
                                    onClick={() =>
                                      navigate(buildFieldUrl(getValue(field, "id", "")))
                                    }
                                  >
                                    Edit
                                  </DropdownItem>
                                  <DropdownItem
                                    className="bulk-dropdown_item"
                                    onClick={() => {
                                      setDeleteId(getValue(field, "id", ""));
                                      setDeleteName(getValue(field, "display_name", ""));
                                      setIsDeleteOpen(true);
                                    }}
                                  >
                                    Disable
                                  </DropdownItem>
                                </DropdownMenu>
                              </UncontrolledDropdown>
                            </div>
                          )}
                        </div>
                      ))}
                    </Draggable>
                  ))}
                  {droppableProvided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </div>
      ) : (
        <div className="table-fields-list__empty">
          <h3>No fields yet</h3>
          <p>Add a column to start configuring this table.</p>
        </div>
      )}
      <DeleteModal
        isOpen={isDeleteOpen}
        handleModal={() => setIsDeleteOpen(false)}
        handleSubmit={handleDelete}
        deleteValue={deleteName}
      />
    </section>
  );
}

export default TableFields;
